import { FormEvent, useEffect, useState } from 'react'
import api from '../../shared/api/request'

type ApiResult<T> = { success: boolean; data: T; error?: string; message?: string }

export type LegalVersions = { terms: string; privacy: string }
export type LegalCurrent = {
  operator: string
  contactEmail: string
  versions: LegalVersions
  documents: Array<{
    type: 'terms' | 'privacy'
    title: string
    version: string
    effectiveDate: string
    contentHash: string
    materialChangeSummary: string
    url: string
  }>
}

function readableError(error: unknown): string {
  if (typeof error === 'object' && error && 'error' in error) {
    return String((error as { error?: string }).error || '操作失败')
  }
  return typeof error === 'string' ? error : '操作失败，请稍后重试。'
}

export function useLegalCurrent() {
  const [current, setCurrent] = useState<LegalCurrent | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    void api.get('/legal/current')
      .then(result => setCurrent((result as unknown as ApiResult<LegalCurrent>).data))
      .catch(err => setError(readableError(err)))
  }, [])
  return { current, error }
}

export function LegalConsent({
  accepted,
  onChange,
  compact = false
}: {
  accepted: boolean
  onChange: (accepted: boolean, versions: LegalVersions | null) => void
  compact?: boolean
}) {
  const { current, error } = useLegalCurrent()
  return <div className={`legal-consent ${compact ? 'compact' : ''}`}>
    <label className="consent-check">
      <input
        type="checkbox"
        checked={accepted}
        disabled={!current}
        onChange={event => onChange(event.target.checked, current?.versions || null)}
      />
      <span>
        我已阅读并同意
        <a href="/legal/terms" target="_blank" rel="noreferrer">《用户服务协议》</a>
        和
        <a href="/legal/privacy" target="_blank" rel="noreferrer">《隐私政策》</a>
      </span>
    </label>
    {current && <small>当前版本 {current.versions.terms}，确认记录会保存在服务器，重大变更时会重新提示。</small>}
    {error && <small className="error-copy">{error}</small>}
  </div>
}

function LegalText({ content }: { content: string }) {
  return <div className="legal-copy">
    {content.split(/\r?\n/).map((line, index) => {
      const value = line.trim()
      if (!value) return <div className="legal-spacer" key={index} />
      if (value.startsWith('# ')) return <h1 key={index}>{value.slice(2)}</h1>
      if (value.startsWith('## ')) return <h2 key={index}>{value.slice(3)}</h2>
      if (value.startsWith('### ')) return <h3 key={index}>{value.slice(4)}</h3>
      return <p key={index}>{value}</p>
    })}
  </div>
}

export function LegalDocumentPage({ type }: { type: 'terms' | 'privacy' }) {
  const { current, error } = useLegalCurrent()
  const [document, setDocument] = useState<any>(null)
  const [documentError, setDocumentError] = useState('')
  useEffect(() => {
    if (!current) return
    const version = current.versions[type]
    void api.get(`/legal/documents/${type}/${version}`)
      .then(result => setDocument((result as unknown as ApiResult<any>).data))
      .catch(err => setDocumentError(readableError(err)))
  }, [current, type])
  return <main className="legal-page">
    <header className="legal-page-head">
      <a href="/" className="legal-home">返回部务云</a>
      <div>
        <strong>部务云</strong>
        <span>校园部门协作平台</span>
      </div>
    </header>
    <article>
      {(error || documentError) && <div className="alert error">{error || documentError}</div>}
      {!document && !error && !documentError && <div className="loading">正在加载法律文档...</div>}
      {document && <>
        <div className="legal-meta">
          <span>版本 {document.version}</span>
          <span>生效日期 {document.effectiveDate}</span>
          <span>联系邮箱 {document.contactEmail}</span>
        </div>
        <LegalText content={document.content} />
      </>}
    </article>
  </main>
}

export function LegalGate({
  onAccepted,
  onExit
}: {
  onAccepted: () => void
  onExit: () => void
}) {
  const { current, error: loadError } = useLegalCurrent()
  const [accepted, setAccepted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!accepted || !current) return
    setBusy(true); setError('')
    try {
      await api.post('/legal/accept', {
        accepted: true,
        versions: current.versions,
        channel: window.location.pathname.startsWith('/platform') ? 'platform_first_login' : 'first_login'
      })
      onAccepted()
    } catch (err) {
      setError(readableError(err))
    } finally {
      setBusy(false)
    }
  }
  return <main className="legal-gate">
    <form className="legal-gate-panel" onSubmit={submit}>
      <p className="eyebrow">首次使用确认</p>
      <h1>使用部务云前，请先阅读相关条款</h1>
      <p>部务云目前为免费、封闭试点，不代表学校官方系统或学校授权。确认记录保存在服务器，换设备后无需重复确认；重大条款变更时会再次提示。</p>
      <div className="legal-link-grid">
        <a href="/legal/terms" target="_blank" rel="noreferrer"><strong>用户服务协议</strong><span>服务边界、账号责任、部门核验与数据规则</span></a>
        <a href="/legal/privacy" target="_blank" rel="noreferrer"><strong>隐私政策</strong><span>信息清单、保存期限、第三方与用户权利</span></a>
      </div>
      <LegalConsent accepted={accepted} onChange={value => setAccepted(value)} />
      {(loadError || error) && <div className="alert error">{loadError || error}</div>}
      <div className="legal-gate-actions">
        <button type="button" className="secondary" onClick={onExit}>不同意并退出</button>
        <button className="primary" disabled={!accepted || !current || busy}>{busy ? '正在确认...' : '同意并继续'}</button>
      </div>
      <small>协议不能排除运营者依法应承担的责任。如有疑问，请联系 1261086613@qq.com。</small>
    </form>
  </main>
}

export function DepartmentVerificationGate({
  userName,
  onExit
}: {
  userName: string
  onExit: () => void
}) {
  const [status, setStatus] = useState<any>(null)
  const [contact, setContact] = useState('')
  const [note, setNote] = useState('')
  const [proof, setProof] = useState<{ name: string; base64: string } | null>(null)
  const [sensitiveConsent, setSensitiveConsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  async function load() {
    try {
      const result = await api.get('/legal/department-verification') as unknown as ApiResult<any>
      setStatus(result.data)
      setContact(result.data.request?.contact || '')
      setNote(result.data.request?.note || '')
    } catch (err) { setError(readableError(err)) }
  }
  useEffect(() => { void load() }, [])
  function chooseFile(file?: File) {
    if (!file) { setProof(null); return }
    if (file.size > 2 * 1024 * 1024) { setError('证明文件不能超过2MB。'); return }
    const reader = new FileReader()
    reader.onload = () => setProof({ name: file.name, base64: String(reader.result || '') })
    reader.readAsDataURL(file)
  }
  async function submit(event: FormEvent) {
    event.preventDefault()
    if (proof && !sensitiveConsent) { setError('请先确认已遮盖无关敏感信息。'); return }
    setBusy(true); setError(''); setMessage('')
    try {
      const result = await api.post('/legal/department-verification', {
        contact,
        note,
        proofName: proof?.name,
        proofBase64: proof?.base64,
        sensitiveConsent
      }) as unknown as ApiResult<any>
      setMessage(result.message || '核验资料已提交。')
      setProof(null)
      await load()
    } catch (err) { setError(readableError(err)) } finally { setBusy(false) }
  }
  return <main className="verification-gate">
    <section className="verification-panel">
      <div className="verification-head"><div><p className="eyebrow">部门待核验</p><h1>{status?.departmentName || '新建部门'}</h1></div><button className="text-btn" onClick={onExit}>退出</button></div>
      <p>{userName}，部门已创建，但核验通过前不能邀请成员或使用任务、排班、活动、财务和交接功能。核验仅用于降低冒用风险，不代表学校官方认证或授权。</p>
      {status?.request?.submittedAt && <div className="alert">资料已提交，正在等待平台管理员人工核验。</div>}
      <form className="stack-form" onSubmit={submit}>
        <label>负责人联系方式<input value={contact} onChange={event => setContact(event.target.value)} placeholder="手机号或邮箱" required /></label>
        <label>部门与本人关系说明<textarea value={note} onChange={event => setNote(event.target.value)} maxLength={1000} placeholder="简要说明所在组织、负责人身份和可联系时间" /></label>
        <label>脱敏证明（可选，PNG/JPG/PDF，2MB以内）
          <input type="file" accept=".png,.jpg,.jpeg,.pdf" onChange={event => chooseFile(event.target.files?.[0])} />
        </label>
        {proof && <label className="consent-check">
          <input type="checkbox" checked={sensitiveConsent} onChange={event => setSensitiveConsent(event.target.checked)} />
          <span>我确认已遮盖无关身份证号、银行卡号、家庭住址和诊疗细节，并同意平台仅为部门核验处理该证明。</span>
        </label>}
        {message && <div className="alert">{message}</div>}
        {error && <div className="alert error">{error}</div>}
        <button className="primary" disabled={busy}>{busy ? '正在提交...' : '提交核验资料'}</button>
      </form>
      <small>联系方式：1261086613@qq.com</small>
    </section>
  </main>
}

export function AccountDeletionPanel({ onChanged }: { onChanged: () => void }) {
  const [request, setRequest] = useState<any>(null)
  const [legalStatus, setLegalStatus] = useState<any>(null)
  const [sensitive, setSensitive] = useState<Record<string, boolean>>({})
  const [password, setPassword] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  async function load() {
    const [deletionResult, legalResult, turnstileResult, timetableResult, proofResult] = await Promise.all([
      api.get('/account/deletion-requests/current'),
      api.get('/legal/status'),
      api.get('/legal/sensitive-consents/turnstile_cross_border'),
      api.get('/legal/sensitive-consents/timetable'),
      api.get('/legal/sensitive-consents/availability_proof')
    ]) as unknown as Array<ApiResult<any>>
    setRequest(deletionResult.data)
    setLegalStatus(legalResult.data)
    setSensitive({
      turnstile_cross_border: Boolean(turnstileResult.data?.accepted),
      timetable: Boolean(timetableResult.data?.accepted),
      availability_proof: Boolean(proofResult.data?.accepted)
    })
  }
  useEffect(() => { void load().catch(() => undefined) }, [])
  async function create() {
    if (!window.confirm('提交后将进入7日冷静期，期间业务功能暂停。确认继续吗？')) return
    try {
      await api.post('/account/deletion-requests', { password, reason })
      setPassword(''); setMessage('注销申请已提交。'); setError('')
      await load(); onChanged()
    } catch (err) { setError(readableError(err)) }
  }
  async function cancel() {
    try {
      await api.post(`/account/deletion-requests/${request.id}/cancel`)
      setMessage('注销申请已撤回。'); setError('')
      await load(); onChanged()
    } catch (err) { setError(readableError(err)) }
  }
  async function revoke(category: 'turnstile_cross_border' | 'timetable' | 'availability_proof') {
    try {
      const result = await api.post('/legal/sensitive-consents', { category, accepted: false }) as unknown as ApiResult<any>
      setMessage(result.message || '非必要授权已撤回。')
      setError('')
      await load()
    } catch (err) { setError(readableError(err)) }
  }
  return <section className="profile-legal">
    <h3>协议与隐私</h3>
    <p><a href="/legal/terms" target="_blank" rel="noreferrer">查看用户服务协议</a> · <a href="/legal/privacy" target="_blank" rel="noreferrer">查看隐私政策</a></p>
    {legalStatus?.acceptedAt && <p>当前版本确认时间：{new Date(legalStatus.acceptedAt).toLocaleString('zh-CN')}（协议 {legalStatus.acceptedVersions?.terms} / 隐私政策 {legalStatus.acceptedVersions?.privacy}）</p>}
    <div className="legal-consent-list">
      <strong>非必要或敏感信息授权</strong>
      <p>撤回后只停止后续处理；依法需要留存的既有记录不会立即删除，再次使用对应功能时会重新征求确认。</p>
      {sensitive.turnstile_cross_border && <button type="button" className="secondary" onClick={() => void revoke('turnstile_cross_border')}>撤回人机验证授权</button>}
      {sensitive.timetable && <button type="button" className="secondary" onClick={() => void revoke('timetable')}>撤回课表处理授权</button>}
      {sensitive.availability_proof && <button type="button" className="secondary" onClick={() => void revoke('availability_proof')}>撤回请假证明处理授权</button>}
      {!Object.values(sensitive).some(Boolean) && <small>当前没有可撤回的非必要授权。</small>}
    </div>
    {request ? <>
      <div className="alert error">账号正在注销冷静期，预计于 {new Date(request.scheduledAt).toLocaleString('zh-CN')} 执行。</div>
      <button type="button" className="secondary" onClick={() => void cancel()}>撤回注销申请</button>
    </> : <div className="stack-form compact-form">
      <label>注销原因（可选）<textarea value={reason} onChange={event => setReason(event.target.value)} maxLength={500} /></label>
      <label>输入当前密码确认<input type="password" value={password} onChange={event => setPassword(event.target.value)} /></label>
      <button type="button" className="danger" disabled={!password} onClick={() => void create()}>申请注销全局账号</button>
    </div>}
    {message && <div className="alert">{message}</div>}
    {error && <div className="alert error">{error}</div>}
  </section>
}

export function AccountDeletionGate({ onCancelled, onExit }: { onCancelled: () => void; onExit: () => void }) {
  return <main className="legal-gate">
    <section className="legal-gate-panel">
      <p className="eyebrow">账号注销冷静期</p>
      <h1>业务功能已暂停</h1>
      <p>你的账号已提交注销申请。7日冷静期内可以撤回；期满后，可删除资料会被删除或匿名化，依法需要保留的审计和争议记录将限制处理。</p>
      <AccountDeletionPanel onChanged={onCancelled} />
      <button type="button" className="text-btn" onClick={onExit}>退出登录</button>
    </section>
  </main>
}
